/**
 * Configuration for reviewer and tester access overrides.
 * Update these lists with actual usernames to grant specific access levels.
 */

// Usernames that should have full "Supporter/Activated" access (Full Access)
export const SUPPORTER_TESTER_USERNAMES = [
  'supporter-tester1',
  'supporter-tester2',
  'supporter-tester'
];

// Usernames that should be treated as "Registered" (2 puzzles) even if they were sponsored
export const REGISTERED_TESTER_USERNAMES = [
  'registered-tester1',
  'registered-tester2',
  'registered-tester'
];

// Usernames that should be treated as "Guest" accounts (1 puzzle) even if logged in
export const GUEST_TESTER_USERNAMES = [
  'reviewer-guest',
  'guest-tester'
];

/**
 * Checks if a username is in the supporter tester list.
 */
export const isSupporterTester = (username?: string): boolean => {
  if (!username) return false;
  return SUPPORTER_TESTER_USERNAMES.some(e => e.toLowerCase() === username.toLowerCase());
};

/**
 * Checks if a username is in the registered tester list.
 */
export const isRegisteredTester = (username?: string): boolean => {
  if (!username) return false;
  return REGISTERED_TESTER_USERNAMES.some(e => e.toLowerCase() === username.toLowerCase());
};

/**
 * Checks if a username is in the guest tester list.
 */
export const isGuestTester = (username?: string): boolean => {
  if (!username) return false;
  return GUEST_TESTER_USERNAMES.some(e => e.toLowerCase() === username.toLowerCase());
};

/**
 * Applies overrides to the user object based on their username.
 */
export const applyUserOverrides = (user: any): any => {
  if (!user || !user.username) return user;
  
  const username = user.username.toLowerCase();
  
  if (isGuestTester(username)) {
    return { ...user, isGuest: true };
  }
  
  // Registered testers are NOT guests
  if (isRegisteredTester(username)) {
    return { ...user, isGuest: false };
  }

  // Supporter testers are NOT guests
  if (isSupporterTester(username)) {
    return { ...user, isGuest: false };
  }
  
  return user;
};

/**
 * Determines the sponsorship status based on the user's username.
 */
export const getOverrideSponsorshipStatus = (username?: string, currentStatus: boolean = false): boolean => {
  if (isSupporterTester(username)) {
    return true;
  }
  // If they are a guest or registered tester, they definitely shouldn't be sponsored (unless they paid)
  // For testing purposes, we force them to NOT be sponsored if they are in these lists
  if (isGuestTester(username) || isRegisteredTester(username)) {
    return false;
  }
  return currentStatus;
};
